import { useState } from "react"
import { Cross2Icon } from "@radix-ui/react-icons"
import { Button } from "@/components/chadcn/Button"

type MedicationRemindersProps = {
  reminderTimes: string[]
  onChange: (reminderTimes: string[]) => void
  disabled?: boolean
}

// Sortable HH:mm comparator — string compare works for zero-padded 24h times.
const sortTimes = (times: string[]): string[] => [...times].sort()

const formatChipLabel = (time24h: string): string => {
  const [hours, minutes] = time24h.split(":").map(Number)
  const period = hours >= 12 ? "pm" : "am"
  const hours12 = hours % 12 === 0 ? 12 : hours % 12
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`
}

const MedicationReminders = ({
  reminderTimes,
  onChange,
  disabled,
}: MedicationRemindersProps) => {
  const [draftTime, setDraftTime] = useState("")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const addDraft = () => {
    if (!draftTime) return
    if (reminderTimes.includes(draftTime)) {
      setErrorMessage("This reminder already exists")
      return
    }
    setErrorMessage(null)
    onChange(sortTimes([...reminderTimes, draftTime]))
    setDraftTime("")
  }

  const removeReminder = (time: string) => {
    onChange(reminderTimes.filter((it) => it !== time))
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <input
          type="time"
          value={draftTime}
          onChange={(e) => {
            setDraftTime(e.target.value)
            setErrorMessage(null)
          }}
          disabled={disabled}
          aria-label="Select a time"
          aria-invalid={!!errorMessage}
          className="flex-1 rounded-lg border-0 px-3 py-2 text-base ring-1 ring-inset ring-input focus:ring-2 focus:ring-primary disabled:text-primary-contrast/60"
        />
        <Button
          type="button"
          variant="outline"
          onClick={addDraft}
          disabled={disabled || !draftTime}
        >
          Add
        </Button>
      </div>
      {errorMessage && (
        <p className="text-sm text-error" role="alert">
          {errorMessage}
        </p>
      )}
      {reminderTimes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {sortTimes(reminderTimes).map((time) => (
            <span
              key={time}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm text-primary-contrast"
            >
              {formatChipLabel(time)}
              <button
                type="button"
                onClick={() => removeReminder(time)}
                disabled={disabled}
                aria-label={`Remove ${formatChipLabel(time)}`}
                className="rounded-full p-0.5 hover:bg-primary-contrast/10"
              >
                <Cross2Icon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default MedicationReminders
